import { supabase } from '../supabase';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Fuente unica de la duracion de trial (F9). Se unifica en 15 dias gratis.
export const DEFAULT_TRIAL_DURATION_DAYS = 14; // ver DEFAULT_TRIAL_DAYS en data/billingBrandConfig.js

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca (este archivo tambien esta PROTECTED, pero se
// mantiene el mismo patron que setupOnboarding.js / firstSaleOnboarding.js).
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';

// Gate de cohorte del onboarding v2 (Fase 1). Todas las conductas nuevas del
// onboarding se preguntan por esto para NO afectar a las orgs ya creadas: las
// existentes nacen con onboarding_v2_enabled = false (o sin la columna) y siguen
// viendo el comportamiento previo. Solo el alta self-serve nueva lo enciende.
export function isOnboardingV2Enabled(organization) {
  return organization?.onboarding_v2_enabled === true;
}

const BASE_PAYMENT_METHODS = [
  {
    name: 'Efectivo',
    discount_percentage: 0,
    surcharge_percentage: 0,
    commission_percentage: 0,
    accreditation_days: 0,
    accreditation_business_days: false,
  },
  {
    name: 'Transferencia',
    discount_percentage: 0,
    surcharge_percentage: 0,
    commission_percentage: 0,
    accreditation_days: 0,
    accreditation_business_days: false,
  },
  {
    name: 'Tarjeta de debito',
    discount_percentage: 0,
    surcharge_percentage: 0,
    commission_percentage: 2.5,
    accreditation_days: 1,
    accreditation_business_days: true,
  },
  {
    name: 'Tarjeta de credito',
    discount_percentage: 0,
    surcharge_percentage: 0,
    commission_percentage: 4,
    accreditation_days: 7,
    accreditation_business_days: true,
  },
  {
    // Fuente unica de medios de cobro (F2). Mercado Pago vivia solo en el seed
    // duplicado de RegistroOnboardingPlan; al unificar la siembra lo traemos aca
    // para que siga siendo medio de cobro por defecto en toda alta nueva.
    name: 'Mercado Pago',
    discount_percentage: 0,
    surcharge_percentage: 0,
    commission_percentage: 6,
    accreditation_days: 0,
    accreditation_business_days: false,
  },
];

const BASE_INVOICE_TYPES = [
  { name: 'TICKET (NO FISCAL)', code: 'T', action: 'subtract' },
  { name: 'FACTURA A', code: 'FA', action: 'subtract' },
  { name: 'NOTA DE DEBITO A', code: 'NDA', action: 'subtract' },
  { name: 'NOTA DE CREDITO A', code: 'NCA', action: 'add' },
  { name: 'RECIBO A', code: 'RA', action: 'neutral' },
  { name: 'FACTURA B', code: 'FB', action: 'subtract' },
  { name: 'NOTA DE DEBITO B', code: 'NDB', action: 'subtract' },
  { name: 'NOTA DE CREDITO B', code: 'NCB', action: 'add' },
  { name: 'RECIBO B', code: 'RB', action: 'neutral' },
  { name: 'FACTURA C', code: 'FC', action: 'subtract' },
  { name: 'NOTA DE DEBITO C', code: 'NDC', action: 'subtract' },
  { name: 'NOTA DE CREDITO C', code: 'NCC', action: 'add' },
  { name: 'RECIBO C', code: 'RC', action: 'neutral' },
  { name: 'REMITO', code: 'R', action: 'neutral' }, // Remitos por defecto no descuentan stock financiero, sólo movimiento físico pero dependerá de la config.
  { name: 'PRESUPUESTO', code: 'PRES', action: 'neutral' },
];

export const BUSINESS_PRESETS = {
  comercio: {
    key: 'comercio',
    label: 'Comercio general',
    shortLabel: 'Comercio',
    description: 'Ideal para kioscos, almacenes y dieteticas. Cobro rapido en mostrador, control de stock simple y actualizacion agil de precios.',
    cardTags: ['CAJA RAPIDA', 'STOCK SIMPLE'],
    cardCta: 'Iniciar con caja rapida',
    storeType: 'retail',
    flags: {
      enable_variants: false,
      allow_negative_stock: false,
      allow_zero_sell_price: false,
      enable_misc_product: false,
      enable_cost_sync: false,
      show_pos_images: true,
    },
    categories: ['GENERAL', 'PROMOCIONES', 'MOSTRADOR', 'BEBIDAS', 'GOLOSINAS', 'ALMACEN', 'LIMPIEZA', 'PERFUMERIA', 'CIGARRILLOS', 'VARIOS'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' },
    ],
    highlights: [
      {
        title: 'Punto de venta agil',
        description: 'Cobros rapidos, caja y medios de pago listos desde el primer dia.',
      },
      {
        title: 'Catalogo base',
        description: 'Productos, categorias y stock preparados para empezar a cargar mercaderia.',
      },
      {
        title: 'Control operativo',
        description: 'Sucursal principal, cliente generico y configuracion minima para vender.',
      },
    ],
    setupChecklist: [
      'Crear sucursal principal y defaults de venta.',
      'Dejar medios de cobro y comprobantes basicos activos.',
      'Sembrar categorias y unidad base para el catalogo.',
    ],
  },
  indumentaria: {
    key: 'indumentaria',
    label: 'Indumentaria y calzado',
    shortLabel: 'Indumentaria',
    description: 'Pensado para locales de ropa. Controla de forma super facil tus ventas por talle, color, marcas y cambios de temporada.',
    cardTags: ['TALLES Y COLORES', 'LISTAS DE PRECIO'],
    cardCta: 'Iniciar con catalogo de ropa',
    storeType: 'retail',
    flags: {
      enable_variants: true,
      allow_negative_stock: false,
      allow_zero_sell_price: false,
      enable_misc_product: false,
      enable_cost_sync: false,
      show_pos_images: true,
    },
    categories: ['REMERAS', 'PANTALONES', 'CALZADO', 'ABRIGOS', 'ACCESORIOS', 'ROPA INTERIOR', 'DEPORTIVA', 'INFANTIL', 'BOLSOS Y CARTERAS', 'OFERTAS'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' },
    ],
    highlights: [
      {
        title: 'Variantes activas',
        description: 'Cada producto queda listo para trabajar por talle, color o modelo.',
      },
      {
        title: 'Ventas de mostrador',
        description: 'POS, listas de precios y stock inicial alineados al flujo comercial.',
      },
      {
        title: 'Catalogo ordenado',
        description: 'Rubros iniciales para empezar a cargar prendas sin arrancar desde cero.',
      },
    ],
    setupChecklist: [
      'Activar variantes para talle y color.',
      'Crear rubros iniciales de indumentaria.',
      'Completar defaults minimos para ventas y tienda online.',
    ],
  },
  gastronomia: {
    key: 'gastronomia',
    label: 'Gastronomia y viandas',
    shortLabel: 'Gastronomia',
    description: 'Armado para rotiserias, panaderias y viandas. Agiliza la toma de pedidos y controla los costos de tus insumos sin volverte loco.',
    cardTags: ['TOMA DE PEDIDOS', 'RECETAS E INSUMOS'],
    cardCta: 'Iniciar gestion de pedidos',
    storeType: 'gastronomy',
    flags: {
      enable_variants: false,
      allow_negative_stock: true,
      allow_zero_sell_price: false,
      enable_misc_product: true,
      enable_cost_sync: true,
      show_pos_images: true,
    },
    categories: ['PLATOS', 'BEBIDAS', 'POSTRES', 'ENTRADAS', 'PIZZAS', 'EMPANADAS', 'SANDWICHES', 'ENSALADAS', 'COMBOS', 'PROMOCIONES'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' },
      { name: 'KILO', abbreviation: 'kg' },
      { name: 'LITRO', abbreviation: 'lt' },
    ],
    highlights: [
      {
        title: 'Operacion rapida',
        description: 'POS y cobranzas configurados para vender sin friccion desde caja.',
      },
      {
        title: 'Base para cocina',
        description: 'Unidades y parametros pensados para recetas, costos y produccion.',
      },
      {
        title: 'Canal digital alineado',
        description: 'La tienda online queda en modo gastronomia para futuros pedidos web.',
      },
    ],
    setupChecklist: [
      'Configurar tienda online en modo gastronomia.',
      'Activar costo sincronizado y producto libre para operacion flexible.',
      'Crear unidades base para insumos y produccion.',
    ],
  },
  servicios: {
    key: 'servicios',
    label: 'Servicios y consultoria',
    shortLabel: 'Servicios',
    description: 'Perfecto para talleres, esteticas o profesionales. Arma presupuestos al instante, factura tus servicios y gestiona tus clientes sin llevar stock.',
    cardTags: ['PRESUPUESTOS', 'SIN STOCK FISICO'],
    cardCta: 'Iniciar gestion de servicios',
    storeType: 'retail',
    flags: {
      enable_variants: false,
      allow_negative_stock: true,
      allow_zero_sell_price: false,
      enable_misc_product: true,
      enable_cost_sync: false,
      show_pos_images: false,
    },
    categories: ['SERVICIOS', 'ABONOS', 'SOPORTE', 'CONSULTORIA', 'MANTENIMIENTO', 'INSTALACIONES', 'REPARACIONES', 'CAPACITACIONES', 'PRODUCTOS', 'VARIOS'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' },
      { name: 'HORA', abbreviation: 'hs' },
    ],
    highlights: [
      {
        title: 'Presupuestos y cobros',
        description: 'La base queda lista para cotizar, facturar y registrar pagos.',
      },
      {
        title: 'Carga simplificada',
        description: 'Se habilita producto libre para vender servicios sin catalogo complejo.',
      },
      {
        title: 'Clientes primero',
        description: 'Defaults comerciales y de impresion para empezar a operar rapido.',
      },
    ],
    setupChecklist: [
      'Habilitar producto libre para ventas de servicios.',
      'Crear categorias y unidad hora para el catalogo comercial.',
      'Dejar comprobantes y medios de cobro listos para presupuestar y cobrar.',
    ],
  },
};

export const BUSINESS_PRESET_LIST = Object.values(BUSINESS_PRESETS);

const DEFAULT_STORE_THEME = {
  primaryColor: '#0f172a',
  storeBackgroundColor: '#f8fafc',
  cardColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
  modalColors: { bg: '#ffffff', border: '#e5e7eb', text: '#111827' },
  carousel: { enabled: false, slides: [] },
  about: { enabled: false, title: '', text: '', image: '' },
  contactSection: { enabled: true },
  locations: [],
  enabled_payment_methods: [],
  payment_method_labels: {},
};

const DEFAULT_NOTIFICATIONS = { sound: true, push: false, toast: true };

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeLabel = (value = '') =>
  value
    .toString()
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const slugify = (value = '') =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 50);

const mergeBooleanFlag = (currentValue, recommendedValue) => {
  if (typeof currentValue === 'boolean') {
    return currentValue || Boolean(recommendedValue);
  }
  return Boolean(recommendedValue);
};

const firstActiveRow = (rows = []) => rows.find((row) => row?.is_active !== false) || rows[0] || null;

const findByNormalizedField = (rows = [], fieldName, expectedValue) => {
  const normalizedExpected = normalizeLabel(expectedValue);
  return rows.find((row) => normalizeLabel(row?.[fieldName]) === normalizedExpected) || null;
};

const buildStoreSlug = (organizationName, organizationId) => {
  const baseSlug = slugify(organizationName) || `tienda-${organizationId.slice(0, 6)}`;
  return `${baseSlug}-${organizationId.slice(0, 4)}`;
};

const isMissingOrganizationsColumnError = (error, columnName) => {
  const haystack = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`.toLowerCase();
  return haystack.includes(String(columnName || '').toLowerCase()) && haystack.includes('column');
};

export function buildOrganizationTrialWindow({
  startDate = new Date(),
  durationDays = DEFAULT_TRIAL_DURATION_DAYS,
} = {}) {
  const trialStartsAt = new Date(startDate);
  const trialEndsAt = new Date(trialStartsAt);
  trialEndsAt.setDate(trialEndsAt.getDate() + durationDays);

  return {
    trial_starts_at: trialStartsAt.toISOString(),
    trial_ends_at: trialEndsAt.toISOString(),
  };
}

export async function createOrganizationWithOptionalTrial({
  organization,
  select = 'id, name',
  trialDurationDays = DEFAULT_TRIAL_DURATION_DAYS,
} = {}) {
  if (!organization || typeof organization !== 'object') {
    throw new Error('Falta el payload de organizacion para crear la empresa.');
  }

  const basePayload = {
    ...organization,
    name: String(organization?.name || '').trim(),
    email: String(organization?.email || '').trim().toLowerCase(),
    phone: String(organization?.phone || '').trim(),
  };
  const payloadWithTrial = {
    ...basePayload,
    ...buildOrganizationTrialWindow({ durationDays: trialDurationDays }),
  };

  const runInsert = async (payload) =>
    supabase
      .from('organizations')
      .insert([payload])
      .select(select)
      .single();

  const firstAttempt = await runInsert(payloadWithTrial);
  if (!firstAttempt.error) {
    return firstAttempt;
  }

  const missingTrialColumns =
    isMissingOrganizationsColumnError(firstAttempt.error, 'trial_starts_at') ||
    isMissingOrganizationsColumnError(firstAttempt.error, 'trial_ends_at');

  if (!missingTrialColumns) {
    return firstAttempt;
  }

  return runInsert(basePayload);
}

export async function ensureCurrentUserProfileMembership({
  userId,
  organizationId,
  email,
  fullName,
  role = 'admin',
  maxAttempts = 4,
} = {}) {
  if (!userId) {
    throw new Error('Falta userId para vincular el perfil.');
  }

  if (!organizationId) {
    throw new Error('Falta organizationId para vincular el perfil.');
  }

  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedFullName = String(fullName || '').trim();
  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const { data: updatedRows, error: updateError } = await supabase
      .from('profiles')
      .update({
        organization_id: organizationId,
        role,
        full_name: normalizedFullName || null,
        email: normalizedEmail || null,
      })
      .eq('id', userId)
      .select('id, organization_id');

    if (!updateError && Array.isArray(updatedRows) && updatedRows.length > 0) {
      return updatedRows[0];
    }

    if (updateError) {
      lastError = updateError;
    }

    const { data: upsertedProfile, error: upsertError } = await supabase
      .from('profiles')
      .upsert(
        [{
          id: userId,
          user_id: userId,
          organization_id: organizationId,
          email: normalizedEmail || null,
          full_name: normalizedFullName || null,
          role,
          requires_pw_update: false,
        }],
        { onConflict: 'id' },
      )
      .select('id, organization_id')
      .maybeSingle();

    if (!upsertError && upsertedProfile?.organization_id === organizationId) {
      return upsertedProfile;
    }

    if (upsertError) {
      lastError = upsertError;
    }

    await wait(350 * (attempt + 1));
  }

  throw lastError || new Error('No se pudo vincular el perfil con la organizacion.');
}

const isMissingUsersTableError = (error) => {
  const haystack = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''} ${error?.code || ''}`.toLowerCase();
  return (
    haystack.includes('does not exist') ||
    haystack.includes('could not find the table') ||
    haystack.includes('schema cache') ||
    haystack.includes('42p01') || // undefined_table (Postgres)
    haystack.includes('pgrst205') // PostgREST: tabla no encontrada en el schema cache
  );
};

/**
 * Vincula al usuario actual con su organizacion en la tabla fisica `public.users`.
 *
 * Por que existe: en Gastify la funcion RLS `get_my_org_id()` resuelve la organizacion
 * del usuario leyendo `public.users.organization_id`, y el trigger de alta crea esa fila
 * con `organization_id = NULL` (la organizacion todavia no existe al hacer signUp). Si no
 * la actualizamos, el INSERT de todas las tablas semilla del onboarding (sitios, clientes,
 * medios de cobro, etc.) falla el chequeo `organization_id = get_my_org_id()` y el
 * provisioning se corta a la mitad. Se llama de forma incondicional antes del provisioning
 * porque `admin_ensure_profile` puede vincular solo `profiles` y dejar `users` en NULL.
 *
 * Es best-effort: en tenants donde `public.users` no existe la operacion se ignora sin
 * lanzar error. La politica RLS de `public.users` permite que cada usuario edite su propia
 * fila (`id = auth.uid()`), por lo que no necesita privilegios de superadmin.
 */
export async function ensureCurrentUserOrgMembership({
  userId,
  organizationId,
  email,
  fullName,
  role = 'admin',
  maxAttempts = 3,
} = {}) {
  if (!userId || !organizationId) {
    return { linked: false, reason: 'missing-args' };
  }

  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedFullName = String(fullName || '').trim();

  const basePatch = { organization_id: organizationId, role };
  if (normalizedFullName) basePatch.full_name = normalizedFullName;
  if (normalizedEmail) basePatch.email = normalizedEmail;

  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const { data: updatedRows, error: updateError } = await supabase
      .from('users')
      .update(basePatch)
      .eq('id', userId)
      .select('id, organization_id');

    if (!updateError && Array.isArray(updatedRows) && updatedRows.length > 0) {
      return { linked: true, via: 'update' };
    }

    if (updateError) {
      if (isMissingUsersTableError(updateError)) {
        return { linked: false, reason: 'no-users-table' };
      }
      lastError = updateError;
    }

    // La fila puede no existir todavia (timing del trigger): intentamos crearla.
    const { data: upsertedUser, error: upsertError } = await supabase
      .from('users')
      .upsert(
        [{
          id: userId,
          organization_id: organizationId,
          role,
          full_name: normalizedFullName || null,
          email: normalizedEmail || null,
          is_active: true,
        }],
        { onConflict: 'id' },
      )
      .select('id, organization_id')
      .maybeSingle();

    if (!upsertError && upsertedUser?.organization_id === organizationId) {
      return { linked: true, via: 'upsert' };
    }

    if (upsertError) {
      if (isMissingUsersTableError(upsertError)) {
        return { linked: false, reason: 'no-users-table' };
      }
      lastError = upsertError;
    }

    await wait(300 * (attempt + 1));
  }

  if (lastError) {
    console.warn('No se pudo vincular public.users con la organizacion:', lastError.message);
  }
  return { linked: false, reason: 'unresolved', error: lastError };
}

export function getBusinessPreset(presetKey) {
  return BUSINESS_PRESETS[presetKey] || BUSINESS_PRESETS.comercio;
}

export function shouldShowFirstRunOnboarding(organization) {
  const organizationStatus = String(organization?.status || '').toLowerCase();
  if (organizationStatus === 'inactive') return false;

  const onboardingStatus = String(organization?.onboarding_status || 'completed').toLowerCase();
  return onboardingStatus === 'pending' || onboardingStatus === 'configured';
}

export async function fetchOrganizationOnboardingStatus(organizationId) {
  if (!organizationId) {
    throw new Error('Falta organizationId para consultar el onboarding.');
  }

  const { data, error } = await supabase
    .from('organizations')
    .select('id, name, business_type, status, onboarding_status, onboarding_completed_at')
    .eq('id', organizationId)
    .single();

  if (error) throw error;

  return data;
}

// ── H5: Provisioning reanudable ──────────────────────────────────────────────
// El alta del paso 2 (RegistroOnboardingPlan) corre en el browser: si el usuario
// cierra la pestaña a mitad, la organización queda sin sus piezas operativas
// base (sitio, cliente por defecto, condición de pago, lista de precios).
// Estas funciones son idempotentes: crean SOLO lo que falta, así el alta puede
// reintentar y Sistema puede completar lo pendiente al entrar. Nunca pisan
// datos existentes (el resume corre sobre orgs que ya pueden estar operando).

const findOrCreateRow = async ({ table, match, insertRow }) => {
  try {
    let query = supabase.from(table).select('id').limit(1);
    Object.entries(match).forEach(([column, value]) => {
      query = query.eq(column, value);
    });
    const { data: existing } = await query;
    if (existing?.[0]?.id) return existing[0].id;

    const { data: created, error } = await supabase
      .from(table)
      .insert([insertRow])
      .select('id')
      .single();
    if (error) throw error;
    return created?.id || null;
  } catch (error) {
    console.warn(`[onboarding] ensureOrgOperationalDefaults: ${table} falló (no crítico):`, error);
    return null;
  }
};

const insertConfigIfMissing = async (table, row) => {
  try {
    const { data: existing } = await supabase
      .from(table)
      .select('organization_id')
      .eq('organization_id', row.organization_id)
      .maybeSingle();
    if (existing) return; // ya configurado: no pisar lo que el usuario ajustó
    await supabase.from(table).insert([row]);
  } catch (error) {
    console.warn(`[onboarding] ensureOrgOperationalDefaults: ${table} falló (no crítico):`, error);
  }
};

export async function ensureOrgOperationalDefaults({ organizationId, orgName }) {
  if (!organizationId) return null;
  const name = orgName || 'Mi negocio';

  const costCenterId = await findOrCreateRow({
    table: 'centros_costo',
    match: { organization_id: organizationId },
    insertRow: { organization_id: organizationId, nombre: 'GENERAL' },
  });

  const siteId = await findOrCreateRow({
    table: 'sitios',
    match: { organization_id: organizationId },
    insertRow: {
      organization_id: organizationId,
      nombre: 'Casa Central',
      direccion: '',
      cost_center_id: costCenterId,
      is_active: true,
    },
  });

  const clientId = await findOrCreateRow({
    table: 'clients',
    match: { organization_id: organizationId, full_name: 'CONSUMIDOR FINAL' },
    insertRow: {
      organization_id: organizationId,
      full_name: 'CONSUMIDOR FINAL',
      doc_type: 'DNI',
      tax_condition: 'Consumidor Final',
      is_active: true,
    },
  });

  const paymentConditionId = await findOrCreateRow({
    table: 'payment_conditions',
    match: { organization_id: organizationId },
    insertRow: { organization_id: organizationId, name: 'Contado', days_until_due: 0, is_active: true },
  });

  const priceListId = await findOrCreateRow({
    table: 'price_lists',
    match: { organization_id: organizationId },
    insertRow: { organization_id: organizationId, name: 'General', adjustment_percentage: 0, is_active: true },
  });

  await insertConfigIfMissing('printed_reports_config', {
    organization_id: organizationId,
    page_format: 'Ticket 58mm',
    show_company_address: true,
    show_company_cuit: true,
    show_company_phone: true,
    show_client_address: false,
    show_client_cuit_dni: false,
    footer_message: '¡Gracias por tu compra!',
  });

  await insertConfigIfMissing('store_config', {
    organization_id: organizationId,
    slug: buildStoreSlug(name, organizationId),
    store_title: name,
    always_open: true,
    show_stock: true,
    store_type: 'retail',
    default_fulfillment_site_id: siteId,
  });

  // Completar SOLO los defaults que estén en null (no pisar elecciones previas).
  try {
    const { data: orgRow } = await supabase
      .from('organizations')
      .select('default_site_id, default_client_id, default_payment_condition_id, default_price_list_id')
      .eq('id', organizationId)
      .maybeSingle();

    const patch = {};
    if (!orgRow?.default_site_id && siteId) patch.default_site_id = siteId;
    if (!orgRow?.default_client_id && clientId) patch.default_client_id = clientId;
    if (!orgRow?.default_payment_condition_id && paymentConditionId) patch.default_payment_condition_id = paymentConditionId;
    if (!orgRow?.default_price_list_id && priceListId) patch.default_price_list_id = priceListId;

    if (Object.keys(patch).length > 0) {
      await supabase.from('organizations').update(patch).eq('id', organizationId);
    }
  } catch (error) {
    console.warn('[onboarding] ensureOrgOperationalDefaults: update defaults falló (no crítico):', error);
  }

  return {
    cost_center_id: costCenterId,
    site_id: siteId,
    default_client_id: clientId,
    default_payment_condition_id: paymentConditionId,
    default_price_list_id: priceListId,
  };
}

// Chequeo barato al entrar al sistema: si la org quedó con defaults en null
// (alta interrumpida), completa lo faltante. Devuelve true si tuvo que actuar.
export async function resumePendingProvisioning({ organizationId }) {
  if (!organizationId) return false;
  try {
    const { data: orgRow, error } = await supabase
      .from('organizations')
      .select('id, name, default_site_id, default_client_id, default_payment_condition_id, default_price_list_id')
      .eq('id', organizationId)
      .maybeSingle();
    if (error || !orgRow) return false;

    const complete =
      orgRow.default_site_id &&
      orgRow.default_client_id &&
      orgRow.default_payment_condition_id &&
      orgRow.default_price_list_id;
    if (complete) return false;

    await ensureOrgOperationalDefaults({ organizationId, orgName: orgRow.name });
    return true;
  } catch (error) {
    console.warn('[onboarding] resumePendingProvisioning falló (no crítico):', error);
    return false;
  }
}

export async function completeFirstRunOnboarding({
  organizationId,
  actorUserId,
  businessType,
}) {
  if (!organizationId) {
    throw new Error('Falta organizationId para ejecutar el onboarding.');
  }

  const preset = getBusinessPreset(businessType);
  const summary = {
    created: [],
    reused: [],
    updated: [],
  };

  const { data: organization, error: organizationError } = await supabase
    .from('organizations')
    .select(`
      id,
      name,
      full_address,
      phone,
      email,
      default_site_id,
      default_client_id,
      default_payment_condition_id,
      default_price_list_id,
      enable_variants,
      allow_negative_stock,
      allow_zero_sell_price,
      enable_misc_product,
      enable_cost_sync,
      show_pos_images,
      business_type,
      onboarding_status
    `)
    .eq('id', organizationId)
    .single();

  if (organizationError) throw organizationError;

  const [
    costCentersResponse,
    sitesResponse,
    clientsResponse,
    paymentConditionsResponse,
    priceListsResponse,
    paymentMethodsResponse,
    invoiceTypesResponse,
    categoriesResponse,
    unitsResponse,
    storeConfigResponse,
  ] = await Promise.all([
    supabase.from('centros_costo').select('id, nombre').eq('organization_id', organizationId),
    supabase.from('sitios').select('id, nombre, direccion, cost_center_id, is_active').eq('organization_id', organizationId),
    supabase.from('clients').select('id, full_name, is_active').eq('organization_id', organizationId),
    supabase.from('payment_conditions').select('id, name, is_active').eq('organization_id', organizationId),
    supabase.from('price_lists').select('id, name, is_active').eq('organization_id', organizationId),
    supabase.from('payment_methods').select('id, name, is_active').eq('organization_id', organizationId),
    supabase.from('invoice_types').select('id, name, code, action, is_active').eq('organization_id', organizationId),
    supabase.from('product_categories').select('id, name, is_active').eq('organization_id', organizationId),
    supabase.from('units_of_measure').select('id, name, abbreviation').eq('organization_id', organizationId),
    supabase.from('store_config').select('*').eq('organization_id', organizationId).maybeSingle(),
  ]);

  const upstreamErrors = [
    costCentersResponse.error,
    sitesResponse.error,
    clientsResponse.error,
    paymentConditionsResponse.error,
    priceListsResponse.error,
    paymentMethodsResponse.error,
    invoiceTypesResponse.error,
    categoriesResponse.error,
    unitsResponse.error,
    storeConfigResponse.error,
  ].filter(Boolean);

  if (upstreamErrors.length > 0) {
    throw upstreamErrors[0];
  }

  const costCenters = costCentersResponse.data || [];
  const sites = sitesResponse.data || [];
  const clients = clientsResponse.data || [];
  const paymentConditions = paymentConditionsResponse.data || [];
  const priceLists = priceListsResponse.data || [];
  const paymentMethods = paymentMethodsResponse.data || [];
  const invoiceTypes = invoiceTypesResponse.data || [];
  const categories = categoriesResponse.data || [];
  const units = unitsResponse.data || [];
  const existingStoreConfig = storeConfigResponse.data || null;

  let costCenter = findByNormalizedField(costCenters, 'nombre', 'GENERAL') || firstActiveRow(costCenters);
  if (!costCenter) {
    const { data, error } = await supabase
      .from('centros_costo')
      .insert([{ organization_id: organizationId, nombre: 'GENERAL' }])
      .select('id, nombre')
      .single();

    if (error) throw error;
    costCenter = data;
    summary.created.push('Centro de costo GENERAL');
  } else {
    summary.reused.push(`Centro de costo ${costCenter.nombre}`);
  }

  let site =
    sites.find((row) => row.id === organization.default_site_id) ||
    findByNormalizedField(sites, 'nombre', 'CASA CENTRAL') ||
    firstActiveRow(sites);

  if (!site) {
    const { data, error } = await supabase
      .from('sitios')
      .insert([
        {
          organization_id: organizationId,
          nombre: 'CASA CENTRAL',
          direccion: organization.full_address || '',
          cost_center_id: costCenter?.id || null,
          is_active: true,
          depositos: [],
        },
      ])
      .select('id, nombre, direccion, cost_center_id, is_active')
      .single();

    if (error) throw error;
    site = data;
    summary.created.push('Sucursal CASA CENTRAL');
  } else {
    summary.reused.push(`Sucursal ${site.nombre}`);
  }

  let defaultClient =
    clients.find((row) => row.id === organization.default_client_id) ||
    findByNormalizedField(clients, 'full_name', 'CONSUMIDOR FINAL') ||
    firstActiveRow(clients);

  if (!defaultClient) {
    const { data, error } = await supabase
      .from('clients')
      .insert([
        {
          organization_id: organizationId,
          full_name: 'CONSUMIDOR FINAL',
          doc_type: 'DNI',
          tax_condition: 'Consumidor Final',
          is_active: true,
        },
      ])
      .select('id, full_name, is_active')
      .single();

    if (error) throw error;
    defaultClient = data;
    summary.created.push('Cliente generico CONSUMIDOR FINAL');
  } else {
    summary.reused.push(`Cliente base ${defaultClient.full_name}`);
  }

  let defaultPaymentCondition =
    paymentConditions.find((row) => row.id === organization.default_payment_condition_id) ||
    findByNormalizedField(paymentConditions, 'name', 'Contado') ||
    firstActiveRow(paymentConditions);

  if (!defaultPaymentCondition) {
    const { data, error } = await supabase
      .from('payment_conditions')
      .insert([
        {
          organization_id: organizationId,
          name: 'Contado',
          days_until_due: 0,
          is_active: true,
        },
      ])
      .select('id, name, is_active')
      .single();

    if (error) throw error;
    defaultPaymentCondition = data;
    summary.created.push('Condicion de pago Contado');
  } else {
    summary.reused.push(`Condicion de pago ${defaultPaymentCondition.name}`);
  }

  let defaultPriceList =
    priceLists.find((row) => row.id === organization.default_price_list_id) ||
    findByNormalizedField(priceLists, 'name', 'General') ||
    firstActiveRow(priceLists);

  if (!defaultPriceList) {
    const { data, error } = await supabase
      .from('price_lists')
      .insert([
        {
          organization_id: organizationId,
          name: 'General',
          adjustment_percentage: 0,
          is_active: true,
        },
      ])
      .select('id, name, is_active')
      .single();

    if (error) throw error;
    defaultPriceList = data;
    summary.created.push('Lista de precios General');
  } else {
    summary.reused.push(`Lista de precios ${defaultPriceList.name}`);
  }

  for (const paymentMethodSeed of BASE_PAYMENT_METHODS) {
    const existingPaymentMethod = findByNormalizedField(paymentMethods, 'name', paymentMethodSeed.name);
    if (existingPaymentMethod) continue;

    const { error } = await supabase.from('payment_methods').insert([
      {
        organization_id: organizationId,
        is_active: true,
        ...paymentMethodSeed,
      },
    ]);

    if (error) throw error;
    paymentMethods.push({ id: `payment-method-${paymentMethodSeed.name}`, ...paymentMethodSeed, is_active: true });
    summary.created.push(`Metodo de cobro ${paymentMethodSeed.name}`);
  }

  for (const invoiceTypeSeed of BASE_INVOICE_TYPES) {
    const existingInvoiceType = findByNormalizedField(invoiceTypes, 'name', invoiceTypeSeed.name);
    if (existingInvoiceType) continue;

    const { error } = await supabase.from('invoice_types').insert([
      {
        organization_id: organizationId,
        is_active: true,
        ...invoiceTypeSeed,
      },
    ]);

    if (error) throw error;
    invoiceTypes.push({ id: `invoice-type-${invoiceTypeSeed.name}`, ...invoiceTypeSeed, is_active: true });
    summary.created.push(`Comprobante ${invoiceTypeSeed.name}`);
  }

  for (const categoryName of preset.categories) {
    const existingCategory = findByNormalizedField(categories, 'name', categoryName);
    if (existingCategory) continue;

    const { error } = await supabase.from('product_categories').insert([
      {
        organization_id: organizationId,
        name: categoryName,
        is_active: true,
      },
    ]);

    if (error) throw error;
    categories.push({ id: `category-${categoryName}`, name: categoryName, is_active: true });
    summary.created.push(`Categoria ${categoryName}`);
  }

  for (const unitSeed of preset.units) {
    const existingUnit = findByNormalizedField(units, 'name', unitSeed.name);
    if (existingUnit) continue;

    const { error } = await supabase.from('units_of_measure').insert([
      {
        organization_id: organizationId,
        name: unitSeed.name,
        abbreviation: unitSeed.abbreviation,
      },
    ]);

    if (error) throw error;
    units.push({ id: `unit-${unitSeed.name}`, ...unitSeed });
    summary.created.push(`Unidad ${unitSeed.name}`);
  }

  const { error: printedReportsError } = await supabase.from('printed_reports_config').upsert(
    [
      {
        organization_id: organizationId,
        page_format: 'Hoja A4 (Estandar)',
        show_company_address: true,
        show_company_cuit: true,
        show_company_phone: true,
        show_company_email: true,
        show_client_address: true,
        show_client_cuit_dni: true,
        show_client_phone: false,
        show_client_email: false,
        footer_message: `Gracias por elegir ${BRAND_NAME}.`,
      },
    ],
    { onConflict: 'organization_id' },
  );

  if (printedReportsError) throw printedReportsError;
  summary.updated.push('Configuracion inicial de impresion');

  const nextStoreConfig = {
    organization_id: organizationId,
    slug: existingStoreConfig?.slug || buildStoreSlug(organization.name, organizationId),
    store_title: existingStoreConfig?.store_title || organization.name || 'Mi tienda online',
    view_mode: existingStoreConfig?.view_mode || 'grid',
    show_stock: existingStoreConfig?.show_stock ?? true,
    always_open: existingStoreConfig?.always_open ?? true,
    store_type: preset.storeType,
    open_hours: existingStoreConfig?.open_hours || { weekly: {} },
    theme_config: {
      ...DEFAULT_STORE_THEME,
      ...(existingStoreConfig?.theme_config || {}),
    },
    notification_config: {
      ...DEFAULT_NOTIFICATIONS,
      ...(existingStoreConfig?.notification_config || {}),
    },
    delivery_estimation: existingStoreConfig?.delivery_estimation || {
      enabled: preset.storeType === 'gastronomy',
      time: preset.storeType === 'gastronomy' ? 45 : 24,
      unit: preset.storeType === 'gastronomy' ? 'minutos' : 'horas',
    },
    default_fulfillment_site_id:
      existingStoreConfig?.default_fulfillment_site_id || site?.id || null,
  };

  const { error: storeConfigUpsertError } = await supabase
    .from('store_config')
    .upsert([nextStoreConfig], { onConflict: 'organization_id' });

  if (storeConfigUpsertError) throw storeConfigUpsertError;
  summary.updated.push(`Tienda online en modo ${preset.shortLabel}`);

  const organizationPatch = {
    business_type: preset.key,
    onboarding_status: 'configured',
    onboarding_completed_at: null,
    onboarding_completed_by: null,
    default_site_id: site?.id || null,
    default_client_id: defaultClient?.id || null,
    default_payment_condition_id: defaultPaymentCondition?.id || null,
    default_price_list_id: defaultPriceList?.id || null,
    enable_variants: mergeBooleanFlag(organization.enable_variants, preset.flags.enable_variants),
    allow_negative_stock: mergeBooleanFlag(organization.allow_negative_stock, preset.flags.allow_negative_stock),
    allow_zero_sell_price: mergeBooleanFlag(organization.allow_zero_sell_price, preset.flags.allow_zero_sell_price),
    enable_misc_product: mergeBooleanFlag(organization.enable_misc_product, preset.flags.enable_misc_product),
    enable_cost_sync: mergeBooleanFlag(organization.enable_cost_sync, preset.flags.enable_cost_sync),
    show_pos_images: mergeBooleanFlag(organization.show_pos_images, preset.flags.show_pos_images),
  };

  const { error: organizationUpdateError } = await supabase
    .from('organizations')
    .update(organizationPatch)
    .eq('id', organizationId);

  if (organizationUpdateError) throw organizationUpdateError;
  summary.updated.push('Perfil operativo del negocio');

  return {
    preset,
    summary,
    organization: {
      ...organization,
      ...organizationPatch,
    },
  };
}

export async function finalizeFirstRunOnboarding({
  organizationId,
  actorUserId,
}) {
  if (!organizationId) {
    throw new Error('Falta organizationId para finalizar el onboarding.');
  }

  const completionPatch = {
    onboarding_status: 'completed',
    onboarding_completed_at: new Date().toISOString(),
    onboarding_completed_by: actorUserId || null,
  };

  const { error } = await supabase
    .from('organizations')
    .update(completionPatch)
    .eq('id', organizationId);

  if (error) throw error;

  return completionPatch;
}
